import { useEffect } from 'react';
import { Panel, Flex, Button, CellList, CellSimple, CellHeader, Typography, Spinner } from '@maxhub/max-ui';
import { useHousesStore } from '../store/houses.store.ts';
import { useAuthStore } from '../store/auth.store.ts';
import { formatPeriod, getCurrentPeriod } from '../utils/period.ts';
import type { Meter } from '../types/models.ts';

const METER_LABEL: Record<string, string> = {
  cold: '🔵 Холодная вода',
  hot: '🔴 Горячая вода',
};

function MeterCard({
  meter,
  onSubmit,
  onHistory,
}: {
  meter: Meter;
  onSubmit: (meterId: string) => void;
  onHistory: (meterId: string) => void;
}) {
  const lastReading = meter.readings?.[0];
  const period = getCurrentPeriod();
  const alreadySubmitted = lastReading?.period === period;

  return (
    <CellList mode="island" style={{ marginBottom: '0.75rem' }}>
      <CellSimple
        title={METER_LABEL[meter.type] ?? meter.type}
        subtitle={meter.serialNumber ? `№ ${meter.serialNumber}` : undefined}
        after={
          lastReading ? (
            <Flex direction="column" align="flex-end" gap={2}>
              <span style={{ fontSize: '1.75rem', fontWeight: 700 }}>
                {Number(lastReading.value).toFixed(2)}
              </span>
              <Flex align="baseline" gap={4}>
                <span style={{ fontSize: '0.875rem', opacity: 0.6 }}>м³</span>
                <Typography.Label style={{ opacity: 0.5 }}>
                  {formatPeriod(lastReading.period)}
                </Typography.Label>
              </Flex>
            </Flex>
          ) : (
            <Typography.Label style={{ opacity: 0.5 }}>Показаний нет</Typography.Label>
          )
        }
      />
      <Flex gap={8} style={{ padding: '0 1rem 1rem' }}>
        {alreadySubmitted ? (
          <Typography.Label style={{ color: '#2e7d32', fontWeight: 600, alignSelf: 'center' }}>
            ✓ Сдано за {formatPeriod(period)}
          </Typography.Label>
        ) : (
          <Button mode="primary" stretched onClick={() => onSubmit(meter.id)}>
            Подать показание
          </Button>
        )}
        <Button mode="secondary" onClick={() => onHistory(meter.id)}>
          История
        </Button>
      </Flex>
    </CellList>
  );
}

interface DashboardPageProps {
  onSubmitReading: (meterId: string) => void;
  onViewHistory: (meterId: string) => void;
  onManageHouse: (houseId: string) => void;
  onViewReceipts: (houseId: string) => void;
  onOpenAdmin: () => void;
}

export function DashboardPage({
  onSubmitReading,
  onViewHistory,
  onManageHouse,
  onViewReceipts,
  onOpenAdmin,
}: DashboardPageProps) {
  const { houses, selectedHouseId, meters, loading, error, loadHouses, loadMeters, selectHouse } =
    useHousesStore();
  const { reset, role } = useAuthStore();

  const selectedHouse = houses.find((h) => h.id === selectedHouseId) ?? null;
  const houseMeters = selectedHouseId ? (meters[selectedHouseId] ?? []) : [];

  useEffect(() => {
    loadHouses();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedHouseId) {
      loadMeters(selectedHouseId);
    }
  }, [selectedHouseId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh' }}>
        <Spinner size={24} />
      </Panel>
    );
  }

  if (error) {
    return (
      <Panel centeredX centeredY style={{ minHeight: '100vh' }}>
        <Typography.Label style={{ display: 'block', opacity: 0.6 }}>{error}</Typography.Label>
      </Panel>
    );
  }

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      {/* Header */}
      <Flex justify="space-between" align="flex-start" style={{ marginBottom: '0.5rem' }}>
        <div>
          <Typography.Headline variant="large-strong" style={{ display: 'block' }}>
            «Водомерка»
          </Typography.Headline>
          <Typography.Label style={{ display: 'block', opacity: 0.6, marginTop: '0.25rem' }}>
            ТСН «Рокада»
          </Typography.Label>
        </div>
        <Flex gap={6}>
          {role === 'admin' && (
            <Button mode="secondary" appearance="themed" size="small" onClick={onOpenAdmin}>
              ⚙️ Админ
            </Button>
          )}
          <Button mode="tertiary" appearance="negative" size="small" onClick={reset}>
            Выйти
          </Button>
        </Flex>
      </Flex>

      {/* Период */}
      <div style={{ marginBottom: '1rem' }}>
        <Typography.Label
          style={{
            display: 'inline-block',
            background: 'var(--color-bg-secondary, #e8f5e9)',
            borderRadius: 20,
            padding: '0.25rem 0.75rem',
            fontWeight: 600,
          }}
        >
          {formatPeriod(getCurrentPeriod())}
        </Typography.Label>
      </div>

      {/* Нет домов */}
      {houses.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', opacity: 0.6 }}>
          <Typography.Label style={{ display: 'block' }}>У вас нет привязанных домов.</Typography.Label>
          <Typography.Label style={{ display: 'block', marginTop: '0.5rem' }}>
            Обратитесь к администратору для добавления.
          </Typography.Label>
        </div>
      )}

      {/* Выбор дома (если несколько) */}
      {houses.length > 1 && (
        <CellList mode="island" style={{ marginBottom: '1rem' }}>
          <CellHeader>Выберите дом</CellHeader>
          {houses.map((h) => (
            <CellSimple
              key={h.id}
              title={h.buildingNumber ?? h.fullAddress ?? 'Дом'}
              showChevron={h.id !== selectedHouseId}
              onClick={() => selectHouse(h.id)}
              style={{ cursor: 'pointer', fontWeight: h.id === selectedHouseId ? 700 : undefined }}
            />
          ))}
        </CellList>
      )}

      {/* Выбранный дом */}
      {selectedHouse && (
        <>
          <CellList mode="island" style={{ marginBottom: '1rem' }}>
            <CellSimple
              title={selectedHouse.fullAddress ?? `Дом ${selectedHouse.buildingNumber}`}
              subtitle={selectedHouse.myRole === 'primary_owner' ? 'Владелец' : 'Совладелец'}
              after={
                <Flex gap={6}>
                  <Button mode="secondary" size="small" onClick={() => onViewReceipts(selectedHouse.id)}>
                    Квитанции
                  </Button>
                  {selectedHouse.myRole === 'primary_owner' && (
                    <Button mode="secondary" size="small" onClick={() => onManageHouse(selectedHouse.id)}>
                      Управление
                    </Button>
                  )}
                </Flex>
              }
            />
          </CellList>

          {/* Счётчики */}
          {houseMeters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', opacity: 0.6 }}>
              <Typography.Label style={{ display: 'block' }}>Счётчики не добавлены.</Typography.Label>
              {selectedHouse.myRole === 'primary_owner' && (
                <Button
                  mode="primary"
                  style={{ marginTop: '1rem' }}
                  onClick={() => onManageHouse(selectedHouse.id)}
                >
                  Добавить счётчик
                </Button>
              )}
            </div>
          ) : (
            <div>
              {houseMeters.map((meter) => (
                <MeterCard
                  key={meter.id}
                  meter={meter}
                  onSubmit={onSubmitReading}
                  onHistory={onViewHistory}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
